import { useEffect, useState } from 'react';
import { Calendar } from 'react-date-range';
import { LuShield } from 'react-icons/lu';

import { Account } from '@ndvr/portfolio-api';

import { EditableCard } from '@/components/EditableCard';
import { useEditableCardGroup } from '@/components/EditableCardGroupContext';
import { CalendarIcon } from '@/components/Icons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { useUpdateAccountLifestyleProtection } from '@/views/portfolio/account-queries';
import { convertFormDateToServerDate, formatDate, formatLocaleDate, formatPercent } from 'utils';
import { cn } from 'utils/cn';
import { LifestyleProtectionInfo } from 'views/portfolio/PortfolioPage/tabs/target-allocation/types';

interface LifestyleProtectionCardProps {
    accounts: Account[];
    lifestyleProtection: LifestyleProtectionInfo | undefined;
    onUpdate?: (hasUnsavedChanges: boolean) => void;
}

export const LifestyleProtectionCard = ({ accounts, lifestyleProtection, onUpdate }: LifestyleProtectionCardProps) => {
    const { mutate: updateAccountLifestyleProtection, isPending: isMutationPending } =
        useUpdateAccountLifestyleProtection();
    const { isEditing, setEditingCard } = useEditableCardGroup();
    const cardIsEditing = isEditing('target-allocation', 'lifestyle');
    const [datePickerOpen, setDatePickerOpen] = useState(false);
    const [editValues, setEditValues] = useState({
        pinnedAccountGUID: lifestyleProtection?.pinnedAccountGUID || '',
        initialSecuredness: lifestyleProtection?.initialSecuredness || 0.05,
        effectiveTaxRate: lifestyleProtection?.effectiveTaxRate || 0,
        targetEndDate: lifestyleProtection?.targetEndDate || '',
        isEnabled: (lifestyleProtection?.initialSecuredness ?? 0) > 0,
    });

    const wasEnabled = (lifestyleProtection?.initialSecuredness ?? 0) > 0;
    const hasChanges =
        editValues.isEnabled !== wasEnabled ||
        (editValues.isEnabled &&
            (editValues.pinnedAccountGUID !== (lifestyleProtection?.pinnedAccountGUID || '') ||
                editValues.initialSecuredness !== (lifestyleProtection?.initialSecuredness || 0.05) ||
                editValues.effectiveTaxRate !== (lifestyleProtection?.effectiveTaxRate || 0) ||
                editValues.targetEndDate !== (lifestyleProtection?.targetEndDate || '')));

    useEffect(() => {
        // Only report unsaved changes when actively editing AND there are changes
        onUpdate?.(cardIsEditing && hasChanges);
    }, [cardIsEditing, hasChanges, onUpdate]);

    const handleEdit = () => {
        setEditValues({
            pinnedAccountGUID: lifestyleProtection?.pinnedAccountGUID || '',
            initialSecuredness: lifestyleProtection?.initialSecuredness || 0.05,
            effectiveTaxRate: lifestyleProtection?.effectiveTaxRate || 0,
            targetEndDate: lifestyleProtection?.targetEndDate || '',
            isEnabled: (lifestyleProtection?.initialSecuredness ?? 0) > 0,
        });
    };

    const handleCancel = () => {
        // BaseCard will handle the editing state
    };

    const handleSave = () => {
        // When disabled, we need to use the current pinned account or any account if none exists
        const accountGuid = editValues.isEnabled
            ? editValues.pinnedAccountGUID
            : lifestyleProtection?.pinnedAccountGUID || accounts[0]?.accountGUID;

        if (!accountGuid) {
            console.error('No account available to update Lifestyle Protection');
            return;
        }

        // When disabled, use the existing target end date or today as fallback
        const fallbackDate = lifestyleProtection?.targetEndDate || new Date().toISOString().split('T')[0];

        updateAccountLifestyleProtection(
            {
                accountGuid,
                lifestyleProtectionRequest: {
                    initialSecuredness: editValues.isEnabled ? editValues.initialSecuredness.toFixed(3) : '0',
                    estimatedTaxRate: editValues.isEnabled ? editValues.effectiveTaxRate.toString() : '0',
                    targetEndDate: editValues.isEnabled
                        ? convertFormDateToServerDate(editValues.targetEndDate)
                        : convertFormDateToServerDate(fallbackDate),
                },
            },
            {
                onSuccess: () => {
                    setEditingCard('target-allocation', null);
                },
            },
        );
    };

    const renderContent = () => {
        if (cardIsEditing) {
            return (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-600">Pinned Account</div>
                        <Select
                            value={editValues.isEnabled ? editValues.pinnedAccountGUID : 'none'}
                            onValueChange={(value) => {
                                if (value === 'none') {
                                    setEditValues({ ...editValues, isEnabled: false, pinnedAccountGUID: '' });
                                } else {
                                    setEditValues({ ...editValues, isEnabled: true, pinnedAccountGUID: value });
                                }
                            }}
                        >
                            <SelectTrigger className="">
                                <SelectValue placeholder="Select an account" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="none">None (Disable Lifestyle Protection)</SelectItem>
                                {accounts.map((account) => (
                                    <SelectItem key={account.accountGUID} value={account.accountGUID}>
                                        {account.accountName.plainText}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {editValues.isEnabled && (
                        <>
                            <div className="space-y-2">
                                <div className="text-sm font-medium text-gray-600">Initial Securedness (%)</div>
                                <div className="flex items-center gap-4">
                                    <Input
                                        type="number"
                                        step="1"
                                        min="5"
                                        max="100"
                                        value={Math.round(editValues.initialSecuredness * 100)}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            const numValue = parseInt(value, 10);
                                            if (!isNaN(numValue) && numValue >= 5 && numValue <= 100) {
                                                setEditValues({
                                                    ...editValues,
                                                    initialSecuredness: numValue / 100,
                                                });
                                            }
                                        }}
                                        placeholder="5"
                                        className="w-20"
                                    />
                                    <Slider
                                        min={5}
                                        max={100}
                                        step={1}
                                        value={[Math.round(editValues.initialSecuredness * 100)]}
                                        onValueChange={(value) => {
                                            setEditValues({
                                                ...editValues,
                                                initialSecuredness: value[0] / 100,
                                            });
                                        }}
                                        className="flex-1"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <div className="text-sm font-medium text-gray-600">Effective Tax Rate (%)</div>
                                <Select
                                    value={editValues.effectiveTaxRate.toString()}
                                    onValueChange={(value) =>
                                        setEditValues({ ...editValues, effectiveTaxRate: Number(value) })
                                    }
                                >
                                    <SelectTrigger className="">
                                        <SelectValue placeholder="Select tax rate" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="0">0%</SelectItem>
                                        <SelectItem value="0.278">27.8%</SelectItem>
                                        <SelectItem value="0.358">35.8%</SelectItem>
                                        <SelectItem value="0.388">38.8%</SelectItem>
                                        <SelectItem value="0.408">40.8%</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-2">
                                <div className="text-sm font-medium text-gray-600">Target End Date</div>
                                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className={cn(
                                                'w-full justify-start text-left font-normal',
                                                !editValues.targetEndDate && 'text-muted-foreground',
                                            )}
                                        >
                                            <CalendarIcon className="mr-2 h-4 w-4" />
                                            {editValues.targetEndDate ? (
                                                formatDate(editValues.targetEndDate)
                                            ) : (
                                                <span>Pick a date</span>
                                            )}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <Calendar
                                            date={
                                                editValues.targetEndDate
                                                    ? new Date(editValues.targetEndDate)
                                                    : new Date()
                                            }
                                            onChange={(date) => {
                                                setEditValues({
                                                    ...editValues,
                                                    targetEndDate: formatLocaleDate(date),
                                                });
                                                setDatePickerOpen(false);
                                            }}
                                            minDate={new Date()}
                                            maxDate={new Date(new Date().getFullYear() + 50, 11, 31)}
                                        />
                                    </PopoverContent>
                                </Popover>
                            </div>
                        </>
                    )}
                </div>
            );
        }

        if (!lifestyleProtection && !cardIsEditing) {
            return <div className="text-muted-foreground text-center">No Lifestyle Protection</div>;
        }

        const pinnedAccount = accounts?.find(
            (account) => account.accountGUID === lifestyleProtection?.pinnedAccountGUID,
        );
        if (lifestyleProtection) {
            return (
                <div className="space-y-4">
                    <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-600">Pinned Account</div>
                        <div className="font-halyard text-sm">{pinnedAccount?.accountName.plainText}</div>
                    </div>
                    <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-600">Initial Securedness</div>
                        <div className="font-halyard text-sm">
                            {formatPercent(lifestyleProtection.initialSecuredness * 100)}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-600">Effective Tax Rate</div>
                        <div className="font-halyard text-sm">
                            {formatPercent(lifestyleProtection.effectiveTaxRate * 100)}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <div className="text-sm font-medium text-gray-600">Target End Date</div>
                        <div className="font-halyard text-sm">
                            {lifestyleProtection.targetEndDate ? formatDate(lifestyleProtection.targetEndDate) : '-'}
                        </div>
                    </div>
                </div>
            );
        }

        return null;
    };

    return (
        <EditableCard
            title="Lifestyle Protection"
            icon={LuShield}
            headerClassName={cardIsEditing ? undefined : 'bg-gray-200'}
            saving={isMutationPending}
            saveDisabled={
                !hasChanges || (editValues.isEnabled && (!editValues.pinnedAccountGUID || !editValues.targetEndDate))
            }
            editGroup="target-allocation"
            editCardId="lifestyle"
            onEdit={handleEdit}
            onCancel={handleCancel}
            onSave={handleSave}
        >
            {renderContent()}
        </EditableCard>
    );
};
